/**
 * A funnel with a red x in every column header of a `tk-grid`, shown while that column has a filter.
 * Clicking it clears just that column's filter. AG Grid's own filter buttons are hidden by the page
 * (see tk-grid.scss for the look), so this takes their place in the header.
 */
export const FILTER_CLEAR_CLASS = 'tk-filter-clear';

/**
 * Adds a clear button to each header cell under `root` (once per cell) and shows or hides it from the grid's
 * current filter model. Header cells are rebuilt as columns scroll in and out of view, so call this again
 * from the grid's filterChanged, virtualColumnsChanged and firstDataRendered events.
 */
export function syncHeaderFilterClearButtons(root: ParentNode, filterModel: Record<string, unknown> | null | undefined, clearFilter: (colId: string) => void): void {
  const filters = filterModel ?? {};
  // Buttons left in the floating filter row by an older layout.
  root.querySelectorAll<HTMLElement>(`.ag-root .ag-header-row-column-filter .${FILTER_CLEAR_CLASS}`).forEach((button) => button.remove());

  root.querySelectorAll<HTMLElement>('.ag-root .ag-header-row-column .ag-header-cell[col-id]').forEach((cell) => {
    const container = cell.querySelector('.ag-cell-label-container');
    if (!container) {
      return;
    }
    let button = cell.querySelector<HTMLButtonElement>(`.${FILTER_CLEAR_CLASS}`);
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = FILTER_CLEAR_CLASS;
      button.title = 'Clear filter';
      button.setAttribute('aria-label', 'Clear filter');
      button.innerHTML = `<i class="bi bi-funnel ${FILTER_CLEAR_CLASS}__funnel" aria-hidden="true"></i>` + `<i class="bi bi-x ${FILTER_CLEAR_CLASS}__x" aria-hidden="true"></i>`;
      button.addEventListener('click', (event) => {
        // A click on the header cell would sort the column.
        event.stopPropagation();
        clearFilter((event.currentTarget as HTMLElement).dataset['colId'] ?? '');
      });
    }
    // The slot AG Grid's own header filter button uses: first child, which is the right side with row-reverse.
    if (button.parentElement !== container || container.firstElementChild !== button) {
      container.insertBefore(button, container.firstChild);
    }
    const colId = cell.getAttribute('col-id') ?? '';
    button.dataset['colId'] = colId;
    button.classList.toggle('is-hidden', !filters[colId]);
  });
}
